// =====================================================
// SignInModal.jsx user sign-in modal  (v2, OTP edition)
//
// Same props as before, so existing usages keep working:
//   <SignInModal
//     isOpen={...}
//     onClose={...}
//     onSignInSuccess={(email, firstName) => ...}
//   />
//
// What changed inside:
//   * Firebase email+password  ->  email + 6-digit code
//     against the live GoOurHub endpoints (the same ones
//     the vendor gate and /signin use). No passwords,
//     nothing to forget.
//   * On success it calls setSession({email, firstName,
//     token}) which powers hearts, /profile, and any
//     "Hi Mike" display THEN onSignInSuccess (old
//     contract: email is still the first argument) and
//     onClose.
//   * Bonus: works standalone on the /SignInModal route
//     too (isOpen defaults to true; closing goes back).
//
// Sign-up note: with code-based sign-in, signing in IS
// signing up new emails get an account automatically.
// SignUpModal can eventually just render this modal.
// =====================================================

import React, { useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaTimes } from "react-icons/fa";
import { api, setSession } from "./session";
import "./SignInModal.css";
import logo from "../../logo.png";
Modal.setAppElement("#root");

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getApiErrorMessage(error, fallbackMessage) {
  const data = error?.response?.data;

  return (
    data?.message ||
    data?.body?.message ||
    (typeof data === "string" ? data : "") ||
    error?.message ||
    fallbackMessage
  );
}

const SignInModal = ({
  isOpen = true,
  onClose,
  onSignInSuccess,
}) => {
  const navigate = useNavigate();

  const [stage, setStage] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const normalizedEmail = email.trim().toLowerCase();

  const resetAll = () => {
    setStage("email");
    setEmail("");
    setChallengeId("");
    setCode("");
    setError("");
  };

  const close = () => {
    if (onClose) {
      onClose();
      return;
    }
    // Standalone route usage (/SignInModal): go back.
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const sendCode = async () => {
    setError("");

    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    setBusy(true);
    try {
      const response = await api.post("/api/auth/email/start", {
        email: normalizedEmail,
        purpose: "partner-signup",
      });

      const returnedChallengeId = response?.data?.challengeId;
      if (!returnedChallengeId) {
        throw new Error(
          "The server did not return an email challenge ID."
        );
      }

      setChallengeId(returnedChallengeId);
      setCode("");
      setStage("code");
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "We could not send the verification code. Please try again."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    await sendCode();
  };

  const handleCodeSubmit = async (event) => {
    event.preventDefault();
    setError("");

    const cleanCode = code.replace(/\D/g, "");
    if (!challengeId || cleanCode.length !== 6) {
      setError("Enter the 6-digit verification code.");
      return;
    }

    setBusy(true);
    try {
      const response = await api.post("/api/auth/email/verify", {
        challengeId,
        code: cleanCode,
      });
const sessionToken =
  response?.data?.sessionToken;

if (!sessionToken) {
  throw new Error(
    "The server did not return a session token."
  );
}

      const returnedAccount = response?.data?.account || {};
      const rawFirst = String(
        response?.data?.firstName ||
          response?.data?.first_name ||
          returnedAccount?.firstName ||
          returnedAccount?.first_name ||
          ""
      ).trim();
      const firstName = rawFirst
        ? rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1)
        : "";

      // The wire that powers hearts, /profile, "Hi Mike".
     setSession({
  email: normalizedEmail,
  firstName,
  token: sessionToken,
});

      // Old contract preserved: email first. firstName is a
      // bonus second argument for parents that want it.
      if (onSignInSuccess) {
        onSignInSuccess(normalizedEmail, firstName);
      }

      resetAll();
      close();
      // Note: no navigation on success the modal closes in
      // place, and if the visitor got here by tapping a ♥,
      // FavoritesContext completes that save with a toast
      // right on the page they were browsing.
    } catch (err) {
      setError(
        getApiErrorMessage(
          err,
          "The verification code could not be verified."
        )
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={close}
      contentLabel="Sign in"
      className="gsim-card"
      overlayClassName="gsim-overlay"
    >
      <button
        type="button"
        className="gsim-close"
        onClick={close}
        aria-label="Close"
      >
        <FaTimes aria-hidden="true" />
      </button>

      <div className="gsim-brand">
  <div className="gsim-brand__logo-wrap">
    <img
      src={logo}
      alt="GoOurHub"
      className="gsim-brand__logo"
    />
  </div>

  <div>
    <span className="gsim-kicker">
      GOOURHUB
    </span>

    <div className="gsim-brand__micro">
      Your community. One hub.
    </div>
  </div>
</div>

{stage === "email" && (
  <>
    <div className="gsim-welcome">
      <span className="gsim-welcome__emoji">
        👋
      </span>

      <div>
        <h2>
          Welcome to GoOurHub
        </h2>

        <p className="gsim-lede">
          Sign in or create your account in seconds.
        </p>
      </div>
    </div>

    <div className="gsim-trust-row">
      <span>✓ No password</span>
      <span>✓ Secure code</span>
      <span>✓ Free account</span>
    </div>

          <form onSubmit={handleEmailSubmit} className="gsim-form">
            <label htmlFor="gsim-email">Email address</label>
            <input
              id="gsim-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="mike@example.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setError("");
              }}
              disabled={busy}
              autoFocus
            />
            <button
              type="submit"
              className="gsim-btn gsim-btn--primary"
              disabled={busy}
            >
              {busy ? "Sending code…" : "Email me a sign-in code →"}
            </button>
          </form>
        </>
      )}

      {stage === "code" && (
        <>
          <h2>Check your email</h2>
          <p className="gsim-lede">We sent a 6-digit code to</p>
          <p className="gsim-email">{normalizedEmail}</p>

          <form onSubmit={handleCodeSubmit} className="gsim-form">
            <label htmlFor="gsim-code">Verification code</label>
            <input
              id="gsim-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              maxLength={6}
              value={code}
              onChange={(event) => {
                setCode(
                  event.target.value.replace(/\D/g, "").slice(0, 6)
                );
                setError("");
              }}
              disabled={busy}
              autoFocus
            />
            <button
              type="submit"
              className="gsim-btn gsim-btn--primary"
              disabled={busy || code.length !== 6}
            >
              {busy ? (
                "Verifying…"
              ) : (
                <>
                  <FaCheckCircle aria-hidden="true" /> Verify and
                  sign in
                </>
              )}
            </button>
          </form>

          <button
            type="button"
            className="gsim-btn gsim-btn--text"
            onClick={sendCode}
            disabled={busy}
          >
            Resend code
          </button>
          <button
            type="button"
            className="gsim-btn gsim-btn--text"
            onClick={() => {
              setStage("email");
              setCode("");
              setError("");
            }}
            disabled={busy}
          >
            Use a different email
          </button>
        </>
      )}

      {error && (
        <p className="gsim-error" role="alert">
          {error}
        </p>
      )}
    </Modal>
  );
};

export default SignInModal;
